import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { Button, Flex, Stack, Switch, Text, VStack } from '@chakra-ui/react';
import CustomSelect from '@components/general/form/custom-select.component';
import FormInput from '@components/general/form/form-input.component';

import StyledSelect from '@components/general/styled-select.component';
import { userTypeOptions } from '@util/constants';
import { usePeople } from '@services/query-services/people.query-service';
import { useTenantById } from '@services/query-services/tenants.query-service';
import { useUserStore } from '@store/app.store';
import { checkLicenseLimitForActivation } from '@util/helpers/person.helpers';

export default function UserForm({ onFinish, user, isSubmitting }) {
  // https://foraday.atlassian.net/browse/FORA-2001
  // Set name to null to allow backend to derive the name from fn + ln
  const form = useForm({ defaultValues: { ...user, name: null } });
  const {
    register,
    formState: { errors, isDirty }
  } = form;
  const { handleSubmit, reset, control } = form;
  const [manager, setManager] = useState();
  const initialDepartments = user?.departments
    ? user.departments.map((dept) => ({ label: dept, value: dept, id: 'custom' }))
    : [];
  const [departments, setDepartments] = useState(initialDepartments);
  const fromForaDay = !user?.external_id;

  // License limit checking
  const tenant = useUserStore((state) => state.tenant); // Keep this for tenant ID
  const isEnterpriseUser = useUserStore((state) => state.isEnterpriseUser);

  // Fetch fresh tenant data from backend for license checking
  const { data: freshTenant } = useTenantById(tenant?.id);

  const { data: activePeopleData } = usePeople(
    { pageSize: 1000, active: true, tenant_ids: [tenant?.id] },
    { includeTotal: true }
  );

  const managerOptions = activePeopleData?.data || [];
  const currentActiveCount = activePeopleData?.total || 0;
  const { isActiveSwitchDisabled, licenseLimit } = checkLicenseLimitForActivation({
    tenant: freshTenant || tenant, // Use fresh tenant data when available, fallback to cached
    currentActiveCount,
    isEnterpriseUser
  });

  useEffect(() => {
    const curManager = managerOptions.find((option) => option?.id === user?.manager?.id);
    if (curManager) {
      const { name, email, id } = curManager;
      setManager({ label: `${name} <${email}>`, id, email, name });
    }
  }, [user?.manager]);

  useEffect(() => {
    if (isSubmitting === 'success') {
      setDepartments('');
      setManager('');
      reset();
    }
  }, [isSubmitting]);

  // READ ONLY VARIANT OF CONTENT
  const staticContent = (
    <>
      <Text>
        Note: Please visit your Google Workspace or Microsoft Entra page to edit user information.
      </Text>
      {user?.first_name && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            First Name
          </Text>
          <Text>{user.first_name}</Text>
        </VStack>
      )}
      {user?.last_name && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            Last Name
          </Text>
          <Text>{user.last_name}</Text>
        </VStack>
      )}
      {user?.email && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            Email
          </Text>
          <Text>{user.email}</Text>
        </VStack>
      )}

      {user?.title && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            Title
          </Text>
          <Text>{user.title}</Text>
        </VStack>
      )}

      {departments?.length > 0 && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            Departments
          </Text>
          <Text>{departments.map((dept) => dept.value).join(', ')}</Text>
        </VStack>
      )}
      {user?.manager && (
        <VStack
          gap={1}
          alignItems='flex-start'
        >
          <Text
            color='surface.onSurfaceVariant'
            pt='1px'
          >
            Manager
          </Text>
          <Text>{user.manager.name}</Text>
        </VStack>
      )}
    </>
  );

  const editableContent = (
    <>
      <FormInput
        type='text'
        size='md'
        registerProps={{ ...register('first_name', { required: true }) }}
        label='First Name'
        errorMsg='First name is required.'
        formControlProps={{ isInvalid: errors?.first_name }}
        formErrorMessageProps={{ color: 'critical.primary' }}
        bg='surface.containerLow'
      />

      <FormInput
        type='text'
        size='md'
        registerProps={{ ...register('last_name', { required: true }) }}
        label='Last Name'
        errorMsg='Last name is required.'
        formControlProps={{ isInvalid: errors?.last_name }}
        formErrorMessageProps={{ color: 'critical.primary' }}
        bg='surface.containerLow'
      />

      <FormInput
        type='email'
        size='md'
        registerProps={{ ...register('email', { required: true }) }}
        label='Email'
        errorMsg='Email is required.'
        formControlProps={{ isInvalid: errors?.email }}
        formErrorMessageProps={{ color: 'critical.primary' }}
        bg='surface.containerLow'
      />

      <FormInput
        type='text'
        size='lg'
        registerProps={{ ...register('title', { required: false }) }}
        label='Title'
        bg='surface.containerLow'
      />

      <CustomSelect
        isCreatable
        selectProps={{ width: '100%' }}
        placeholder='Departments'
        noOptionsMessage={() => null}
        value={departments}
        onChange={setDepartments}
        chakraStyles={{
          dropdownIndicator: (b) => ({
            ...b,
            display: 'none'
          })
        }}
      />

      <CustomSelect
        selectProps={{ width: '100%' }}
        placeholder='Manager'
        options={managerOptions.map((person) => ({
          value: person.id,
          label: `${person.name} <${person.email}>`,
          name: person.name,
          email: person.email,
          id: person.id
        }))}
        value={manager}
        onChange={(args) => {
          if (args.length > 0) {
            setManager(args.pop());
          } else {
            setManager([]);
          }
        }}
      />
    </>
  );

  return (
    <form
      onSubmit={handleSubmit((values) => {
        onFinish({
          ...values,
          departments: departments.map((dept) => dept.label),
          manager
        });
      })}
    >
      <Stack
        layerStyle='section-card'
        px={{ base: 4, lg: 11 }}
        py={{ base: 8, lg: 11 }}
        gap={10}
      >
        <Flex
          w='100%'
          justifyContent='space-between'
          alignItems='center'
        >
          <Controller
            control={control}
            name='active'
            render={({ field: { onChange, value } }) => (
              <>
                <Switch
                  isChecked={!!value}
                  name='active'
                  onChange={(e) => {
                    // Prevent enabling active if it would exceed the license limit
                    if (e.target.checked && isActiveSwitchDisabled) {
                      return;
                    }
                    onChange(e);
                  }}
                  isDisabled={isActiveSwitchDisabled && !value}
                  data-testid='active-switch'
                />
                <Stack
                  direction={['column', 'row']}
                  justifyContent='space-between'
                  w='100%'
                  mx='10px'
                >
                  <Text
                    fontSize='lg'
                    variant='page-header'
                  >
                    Active
                  </Text>
                  {isActiveSwitchDisabled && !user?.active && (
                    <Text
                      fontSize='sm'
                      color='critical.primary'
                      mt={1}
                      data-testid='limit-reached-txt'
                    >
                      License limit reached ({currentActiveCount}/{licenseLimit}). Cannot activate
                      more users.
                    </Text>
                  )}
                </Stack>
              </>
            )}
          />
        </Flex>
        <Flex
          w='fit-content'
          gap={2}
          direction='column'
        >
          <Controller
            control={control}
            name='type'
            rules={{ validate: (v) => !!v }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <StyledSelect
                  selectLabel='User Type'
                  placeholder='Select User Type'
                  options={userTypeOptions}
                  value={value}
                  onChange={onChange}
                  menuProps={{ style: { width: '50%' } }}
                />
                {error && (
                  <Text
                    fontSize='sm'
                    color='critical.primary'
                  >
                    Please select a user type.
                  </Text>
                )}
              </>
            )}
          />
        </Flex>
        {fromForaDay || !user ? editableContent : staticContent}

        <Button
          type='submit'
          variant='reskinPrimary'
          w={96}
          label='Submit'
          isLoading={isSubmitting === 'submitting'}
          loadingText='Submitting'
          isDisabled={!isDirty}
          data-testid='submit-button'
        >
          Submit
        </Button>
      </Stack>
    </form>
  );
}
